import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import { ControlDeck } from "./components/ControlDeck";
import { PerfHud } from "./components/PerfHud";
import { usePerformance } from "./lib/usePerformance";
import { readSettings, resolveReducedMotion, writeSettings } from "./lib/settings";
import { labs } from "./labs/registry";

const modules = {
  overview: lazy(() => import("./labs/Overview")),
  css: lazy(() => import("./labs/CssLab")),
  motion: lazy(() => import("./labs/MotionLab")),
  gsap: lazy(() => import("./labs/GsapLab")),
  story: lazy(() => import("./labs/StoryLab")),
  vector: lazy(() => import("./labs/VectorLab")),
  canvas: lazy(() => import("./labs/CanvasLab")),
  three: lazy(() => import("./labs/ThreeLab")),
  benchmark: lazy(() => import("./labs/BenchmarkLab")),
};

function routeFromHash() {
  const requested = window.location.hash.replace("#/", "");
  return requested in modules ? (requested as keyof typeof modules) : "overview";
}

export function App() {
  const [route, setRoute] = useState<keyof typeof modules>(routeFromHash);
  const [settings, setSettings] = useState(readSettings);
  const [menuOpen, setMenuOpen] = useState(false);
  const [systemReduced, setSystemReduced] = useState(false);
  const metrics = usePerformance(settings.hud);
  const ActiveLab = modules[route];
  const definition = useMemo(() => labs.find((lab) => lab.id === route)!, [route]);
  const reduced = resolveReducedMotion(settings.profile, systemReduced);

  useEffect(() => {
    const query = matchMedia("(prefers-reduced-motion: reduce)");
    const sync = () => setSystemReduced(query.matches);
    sync();
    query.addEventListener("change", sync);
    return () => query.removeEventListener("change", sync);
  }, []);

  useEffect(() => {
    const onHash = () => {
      setRoute(routeFromHash());
      setMenuOpen(false);
      requestAnimationFrame(() => document.getElementById("lab-main")?.focus());
    };
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  useEffect(() => {
    writeSettings(settings);
    document.documentElement.dataset.motion = reduced ? "reduced" : "full";
    document.documentElement.style.setProperty("--motion-scale", String(settings.intensity));
  }, [settings, reduced]);

  return (
    <div className="app-shell" style={{ "--lab-accent": definition.accent } as React.CSSProperties}>
      <header className="topbar">
        <a className="brand" href="#/overview" aria-label="Experience Lab home">
          <span className="brand-mark">XL</span>
          <span><strong>Experience Lab</strong><small>RENDER / MOVE / MEASURE</small></span>
        </a>
        <button
          className="menu-button"
          aria-expanded={menuOpen}
          aria-controls="lab-navigation"
          onClick={() => setMenuOpen((open) => !open)}
        >
          {menuOpen ? "Close" : "Stations"}
        </button>
        <nav id="lab-navigation" className={menuOpen ? "open" : ""} aria-label="Experiment stations">
          {labs.map((lab) => (
            <a
              key={lab.id}
              href={`#/${lab.id}`}
              aria-current={route === lab.id ? "page" : undefined}
              style={{ "--item-accent": lab.accent } as React.CSSProperties}
            >
              <small>{lab.eyebrow.split(" / ")[0]}</small>
              <span>{lab.title.split(" ")[0]}</span>
            </a>
          ))}
        </nav>
      </header>
      <ControlDeck settings={settings} setSettings={setSettings} />
      {settings.hud && <PerfHud metrics={metrics} />}
      <main id="lab-main" tabIndex={-1}>
        <Suspense fallback={<div className="lab-loading"><span />Loading station…</div>}>
          <ActiveLab settings={settings} reduced={reduced} metrics={metrics} />
        </Suspense>
      </main>
      <footer>
        <span>Experience Lab / v0.1</span>
        <span>Semantic DOM · progressive enhancement · no remote assets</span>
      </footer>
    </div>
  );
}
